import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { availabilityApi, roomsApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { format, addDays, eachDayOfInterval, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { CalendarDays, Search, Grid3X3, ChevronLeft, ChevronRight, Users, Moon, DollarSign, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function AvailabilityPage() {
  const { currentProperty } = useAuth();
  const [activeTab, setActiveTab] = useState('search');
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchParams, setSearchParams] = useState({
    arrival_date: format(new Date(), 'yyyy-MM-dd'),
    departure_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    adults: 2,
    children: 0,
    rooms: 1,
    room_type_id: '',
  });
  const [searchResults, setSearchResults] = useState(null);

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState('');

  useEffect(() => {
    if (currentProperty) {
      roomsApi.listTypes(currentProperty.id).then(res => setRoomTypes(res.data));
    }
  }, [currentProperty]);

  const handleSearch = async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const response = await availabilityApi.search(currentProperty.id, searchParams);
      setSearchResults(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendar = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const startDate = format(startOfMonth(calendarMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(calendarMonth), 'yyyy-MM-dd');
      const response = await availabilityApi.getCalendar(
        currentProperty.id,
        startDate,
        endDate,
        selectedRoomType || undefined
      );
      setCalendarData(response.data);
    } catch (error) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [currentProperty, calendarMonth, selectedRoomType]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      loadCalendar();
    }
  }, [activeTab, loadCalendar]);

  const getAvailabilityColor = (available, total) => {
    if (available === 0) return 'bg-red-100 text-red-800';
    if (available <= total * 0.3) return 'bg-amber-100 text-amber-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="space-y-6" data-testid="availability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Availability</h1>
          <p className="text-muted-foreground">Check room availability and view calendar for {currentProperty?.name}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="search" data-testid="tab-search">
            <Search className="h-4 w-4 mr-2" /> Search Availability
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <Grid3X3 className="h-4 w-4 mr-2" /> Calendar View
          </TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="space-y-6">
          {/* Search Form */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="space-y-2">
                  <Label>Check-in</Label>
                  <Input
                    type="date"
                    value={searchParams.arrival_date}
                    onChange={(e) => setSearchParams({ ...searchParams, arrival_date: e.target.value })}
                    data-testid="search-checkin"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Check-out</Label>
                  <Input
                    type="date"
                    value={searchParams.departure_date}
                    onChange={(e) => setSearchParams({ ...searchParams, departure_date: e.target.value })}
                    data-testid="search-checkout"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adults</Label>
                  <Select value={String(searchParams.adults)} onValueChange={(v) => setSearchParams({ ...searchParams, adults: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Children</Label>
                  <Select value={String(searchParams.children)} onValueChange={(v) => setSearchParams({ ...searchParams, children: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Rooms</Label>
                  <Select value={String(searchParams.rooms)} onValueChange={(v) => setSearchParams({ ...searchParams, rooms: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleSearch} className="w-full" disabled={loading} data-testid="search-btn">
                    <Search className="h-4 w-4 mr-2" /> Search
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Search Results */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}><CardContent className="p-6"><Skeleton className="h-40" /></CardContent></Card>
              ))}
            </div>
          ) : searchResults ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span><CalendarDays className="h-4 w-4 inline mr-1" /> {searchResults.nights} night(s)</span>
                <span><Users className="h-4 w-4 inline mr-1" /> {searchResults.adults} adults, {searchResults.children} children</span>
              </div>

              {searchResults.results.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center" data-testid="no-availability">
                    <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
                    <h3 className="text-lg font-medium">No Availability</h3>
                    <p className="text-muted-foreground mt-2">No rooms available for the selected dates. Try different dates or fewer rooms.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {searchResults.results.map((result) => (
                    <Card key={result.room_type_id} className="overflow-hidden" data-testid={`result-${result.room_type_id}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{result.room_type_name}</CardTitle>
                            <p className="text-sm text-muted-foreground font-mono">{result.room_type_code}</p>
                          </div>
                          <Badge variant="secondary">{result.available} available</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <p className="text-sm text-muted-foreground">{result.room_type_description}</p>
                          
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline">
                              <Users className="h-3 w-3 mr-1" /> Max {result.max_occupancy}
                            </Badge>
                            {result.bed_type && <Badge variant="outline">{result.bed_type}</Badge>}
                            {result.includes_breakfast && <Badge className="bg-amber-500">Breakfast Included</Badge>}
                          </div>

                          <div className="border-t pt-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-muted-foreground">{result.rate_plan_name}</span>
                            </div>
                            <div className="flex items-end justify-between">
                              <div>
                                <div className="text-2xl font-bold">{result.currency} {result.total_amount.toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">
                                  {result.currency} {result.total_before_tax.toLocaleString()} + {result.currency} {result.tax_amount.toLocaleString()} tax
                                </div>
                              </div>
                              <Button data-testid={`book-${result.room_type_id}`}>Book Now</Button>
                            </div>
                          </div>

                          {result.restrictions.length > 0 && (
                            <div className="text-sm text-amber-600">
                              {result.restrictions.map((r, i) => <div key={i}>{r.message}</div>)}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Search for Availability</h3>
                <p className="text-muted-foreground mt-2">Enter your dates and guests to see available rooms.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4">
          {/* Calendar Controls */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="outline" size="icon" onClick={() => setCalendarMonth(m => addDays(m, -30))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="text-lg font-semibold">{format(calendarMonth, 'MMMM yyyy')}</h3>
                  <Button variant="outline" size="icon" onClick={() => setCalendarMonth(m => addDays(m, 30))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Select value={selectedRoomType || 'all'} onValueChange={(v) => setSelectedRoomType(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All Room Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Room Types</SelectItem>
                    {roomTypes.map((rt) => (
                      <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Calendar Grid */}
          {loading ? (
            <Card><CardContent className="p-6"><Skeleton className="h-96" /></CardContent></Card>
          ) : calendarData ? (
            <Card>
              <CardContent className="p-0 overflow-auto">
                <Table data-testid="availability-calendar">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-background z-10 min-w-[150px]">Room Type</TableHead>
                      {calendarData.room_types[0]?.dates.map((d) => (
                        <TableHead key={d.date} className="text-center min-w-[80px]">
                          <div className="font-medium">{format(parseISO(d.date), 'EEE')}</div>
                          <div className="text-xs text-muted-foreground">{format(parseISO(d.date), 'd')}</div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calendarData.room_types.map((rt) => (
                      <TableRow key={rt.room_type_id}>
                        <TableCell className="sticky left-0 bg-background z-10 font-medium">
                          {rt.room_type_name}
                        </TableCell>
                        {rt.dates.map((d) => (
                          <TableCell key={d.date} className="text-center p-1">
                            <div className={cn(
                              'rounded px-2 py-1 text-sm font-medium',
                              getAvailabilityColor(d.available, d.total)
                            )}>
                              {d.available}/{d.total}
                            </div>
                            {d.restrictions.length > 0 && (
                              <div className="mt-1 flex justify-center gap-0.5">
                                {d.restrictions.includes('STOP_SELL') && <Badge variant="destructive" className="text-[10px] px-1">SS</Badge>}
                                {d.restrictions.includes('CTA') && <Badge variant="secondary" className="text-[10px] px-1">CTA</Badge>}
                              </div>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : null}

          {/* Legend */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-100" />
              <span>Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-amber-100" />
              <span>Low Availability</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-100" />
              <span>Sold Out</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="destructive" className="text-[10px] px-1">SS</Badge>
              <span>Stop Sell</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-[10px] px-1">CTA</Badge>
              <span>Closed to Arrival</span>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
